import { useEffect } from 'react';

/** Space kept between a focused control and the bar it would otherwise meet. */
const GAP = 12;

/** True when the element, or an ancestor, is pinned: it does not move when the page scrolls. */
function pinned(el: Element): boolean {
  for (let node: Element | null = el; node; node = node.parentElement) {
    const position = getComputedStyle(node).position;
    if (position === 'fixed' || position === 'sticky') return true;
  }
  return false;
}

/** The bars matching `selector` that are pinned, drawn, and share columns with `rect`. */
function barsBeside(selector: string, rect: DOMRect): DOMRect[] {
  return [...document.querySelectorAll(selector)]
    .filter((bar) => pinned(bar))
    .map((bar) => bar.getBoundingClientRect())
    .filter((b) => b.height > 0 && b.width > 0 && b.left < rect.right && rect.left < b.right);
}

/**
 * Keyboard focus never rests behind the pinned bars (WCAG 2.4.11): the sticky header above the
 * content, and the player bar and phone tab bar at the foot of the screen. After the browser has
 * scrolled a control into view, the page moves just enough to clear any bar that still covers it.
 *
 * CSS scroll padding cannot do this here. The desk rail is sticky, so bottom padding makes the
 * browser scroll the whole page to "reveal" the rail's lower links, and top padding would add to
 * the scroll margins the headings and reading blocks already carry. Only keyboard focus on a
 * control in the page's own flow is moved: a heading focused by script (tabindex -1), a click, and
 * anything inside a bar, the rail or a dialog are left where they are.
 */
export function useFocusClearOfBars(): void {
  useEffect(() => {
    let frame = 0;
    const onFocus = (event: FocusEvent) => {
      const el = event.target;
      if (!(el instanceof HTMLElement) || el.tabIndex < 0) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (document.activeElement !== el || !el.matches(':focus-visible') || pinned(el)) return;
        const rect = el.getBoundingClientRect();
        const top = Math.max(0, ...barsBeside('[data-sticky-bar]', rect).map((b) => b.bottom)) + GAP;
        const bottom =
          Math.min(
            window.innerHeight,
            ...barsBeside('[data-player-bar] > section, [data-tab-bar]', rect).map((b) => b.top),
          ) - GAP;
        // Up to reveal the top edge; down to reveal the bottom edge, never past the top edge.
        const delta =
          rect.top < top
            ? rect.top - top
            : rect.bottom > bottom
              ? Math.min(rect.bottom - bottom, rect.top - top)
              : 0;
        if (delta !== 0) window.scrollBy({ top: delta, behavior: 'instant' });
      });
    };
    document.addEventListener('focusin', onFocus);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('focusin', onFocus);
    };
  }, []);
}
